import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ModelSectionBase } from './model-section-base';
import { t } from '@vitesse/base-ui/i18n';
import { statusBadge } from '@vitesse/base-ui/components/templates';
import { fmtDateTime } from '@vitesse/base-ui/format';
import type { AiApi, ModelActivity } from '@vitesse/base-ui/ai';

/*
 * <vitesse-model-activity> — WHAT this model's AI calls actually did.
 *
 * Arming a statechart says it may act with somebody's integrations while nobody is watching. The
 * page said who accepted that and stopped there, so the obvious next question — "and what has it
 * been doing" — had no answer anywhere near where it is asked. It was answerable only in the AI
 * console, filtered by hand, and only if you already knew the run belonged to this model.
 *
 * Until recently it was not answerable at all: the trail recorded the GATEWAY a machine call came
 * from and that it was an `<invoke>`, but a box runs many models at once. The model id is now on the
 * audit row, which is what makes this section possible rather than merely absent.
 *
 * READ-ONLY, AND NEWEST FEW. This is a glance, not the audit page — enough to see that the thing you
 * armed is behaving, with the console one link away for the rest. Filtering happens in the database,
 * so a busy org does not pay to fetch a page and discard it here.
 */
@customElement('vitesse-model-activity')
export class ModelActivitySection extends ModelSectionBase {
  @state() private rows: ModelActivity[] = [];
  @state() private loaded = false;
  @state() private loadError = false;

  static properties = { ...ModelSectionBase.properties, api: { attribute: false } };
  declare api?: AiApi;

  protected sectionHeading(): string {
    return t('model.aiActivity');
  }
  protected sectionClass(): string {
    return 'model-activity-section';
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.refresh();
  }

  protected updated(changed: Map<string, unknown>): void {
    // The modelId arrives after mount on a deep link, and the api handle can arrive later still —
    // refetch on either rather than showing another model's runs, or none at all.
    if (changed.has('modelId') || (changed.has('api') && !this.loaded)) void this.refresh();
  }

  private async refresh(): Promise<void> {
    if (!this.api || !this.modelId) return;
    const myId = this.modelId;
    let rows: ModelActivity[] | undefined;
    try {
      rows = await this.api.listModelActivity(myId);
    } catch {
      /* surfaced below — a failed trail must not render as "no activity" or as blank forever */
    }
    if (this.modelId !== myId) return; // raced / changed mid-await — the newer call owns the view
    this.loadError = rows === undefined;
    this.rows = rows ?? [];
    this.loaded = true;
  }

  /** A duration a person reads at a glance: milliseconds are noise above a second. */
  private static took(ms: number): string {
    return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
  }

  protected renderContent(): unknown {
    if (!this.api) return html`<div class="home-empty-hint">${t('model.armingUnavailable')}</div>`;
    if (!this.loaded) return nothing;
    if (this.loadError) return html`<div class="base-form-error">${t('model.aiActivityFailed')}</div>`;
    if (this.rows.length === 0) {
      // NOT AN ERROR, and worth saying so: a model that has never invoked looks exactly like a model
      // whose trail failed to load unless the empty state says which it is.
      return html`<div class="home-empty-hint">${t('model.noAiActivity')}</div>`;
    }
    return html`
      <div class="mact-list">
        ${this.rows.map(
          (r) => html`
            <div class="mact-row">
              <span class="mact-state">
                ${r.failed ? statusBadge('red', r.outcome || t('model.aiFailed')) : statusBadge('green', t('model.aiOk'))}
              </span>
              <span class="mact-what">
                <span class="mact-expert">${r.expertId}</span>
                <!-- The model's own statement of intent, which is what a reader recognises. The
                     summary is what it got back, and is the longer half — so intent leads. -->
                <span class="mact-objective">${r.objective || r.summary}</span>
              </span>
              <!-- fmtDateTime, not toLocaleString: the host's locale is the SERVER's in a worker and
                   the browser's here, and a date that changes shape by where it renders is not a
                   date anyone can compare. -->
              <span class="mact-when" title=${fmtDateTime(r.atMs)}> ${ModelActivitySection.took(r.latencyMs)} </span>
            </div>
          `,
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-model-activity': ModelActivitySection;
  }
}
